/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../../assets/ts/_utils'
import { useThemeMode } from '../../../layout/theme-mode/ThemeModeProvider'
import { DatePicker } from 'antd'
import InfoPopover from '../popup/InfoPopover'

interface chartDataType {
  month: number[]
  values_success: number[]
  values_unsuccess: number[]
}

type Props = {
  className: string
  chartData: chartDataType
  total: number
}

const ChartsWidgetSMSStats: React.FC<Props> = ({ className, chartData, total }) => {
  const intl = useIntl()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const failedLabel = intl.formatMessage({ id: 'SMS.FAILED', defaultMessage: 'Failed' })
  const successLabel = intl.formatMessage({ id: 'SMS.SUCCESS', defaultMessage: 'Success' })

  const refreshMode = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(chartRef.current, getChartOptions(height, chartData, failedLabel, successLabel))
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshMode()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, chartData])

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <InfoPopover iconClassName='bi bi-info-circle cursor-pointer text-primary fs-3' backgroundColor='#1591EA' popoverContent={
            <p className='m-0'>
              <b>{intl.formatMessage({ id: 'SMS.DISCLAIMER_TITLE', defaultMessage: 'Disclaimer⚠️' })}</b>:
              <br />
              {intl.formatMessage({ id: 'SMS.DISCLAIMER_TEXT', defaultMessage: 'The SMS statistics published are solely for traffic analysis and are not intended for billing purposes.' })}
            </p>
          }>
            <span className='card-label fw-bold fs-3 mb-1'>
              {intl.formatMessage({ id: 'SMS.SENT_STATS', defaultMessage: 'SMS Sent Stats' })}
            </span>
          </InfoPopover>

          <span className='text-muted fw-semibold fs-7'>
            {intl.formatMessage({ id: 'SMS.TOTAL_SENT', defaultMessage: 'Total {total} SMS Sent' }, { total })}
          </span>
        </h3>
      </div>

      <div className='card-body p-4'>
        <div ref={chartRef} id='kt_charts_widget_3_chart' style={{ height: '350px' }}></div>
      </div>
    </div>
  )
}

export { ChartsWidgetSMSStats }

function getChartOptions(height: number, chartData: chartDataType, failedLabel = 'Failed', successLabel = 'Success'): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const successColor = getCSSVariableValue('--bs-success')
  const failedColor = getCSSVariableValue('--bs-danger')

  return {
    series: [
      {
        name: failedLabel,
        data: chartData?.values_unsuccess,
      },
      {
        name: successLabel,
        data: chartData?.values_success,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    colors: [failedColor, successColor],
    stroke: {
      curve: 'straight',
      show: true,
      width: 2,
    },
    xaxis: {
      categories: chartData?.month,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false
      },
      title: {
        text: '',
        offsetY: 5,
        style: {
          color: labelColor,
          fontSize: '14px',
          fontWeight: 600,
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: labelColor,
          width: 1,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'horizontal',
        gradientToColors: [failedColor, successColor],
        stops: [0, 100],
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: 'light',
      y: {
        formatter: (val: any) => val,
      },
      x: {
        formatter: (value: any) => {
          const day = chartData?.month[value - 1] || '';
          return `${day}`;
        },
      },
    },
  }
}
